import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { useState } from 'react'

import {
  PriceListProductsResponse,
  UpdateProductBody,
} from '@/api/_schemas/management'
import { updateProduct } from '@/api/management'
import { FormUpdateProduct } from '@/app/panel/management/price-list/_schemas/update-product'
import { ApiError } from '@/config/api'
import { keyStore } from '@/config/key-store'
import { queryClient } from '@/config/query-client'
import { useToast } from '@/hooks/use-toast'
import { FormError, handleErrors } from '@/services/fetch'

export const useUpdateProduct = () => {
  const [formErrors, setFormErrors] = useState<FormError[]>([])

  const { toastDestructive, toastPositive } = useToast()

  const mutation = useMutation({
    mutationFn: async (data: UpdateProductBody) =>
      await updateProduct(data, { id: data.cod_produto }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [...keyStore.priceList.product.queryKey, data.cod_produto],
      })

      queryClient
        .getQueryCache()
        .findAll({
          predicate: ({ queryKey }) =>
            Array.isArray(queryKey) &&
            [...keyStore.priceList.products.queryKey].includes(queryKey[0]),
        })
        .forEach(({ queryKey }) => {
          queryClient.setQueryData<PriceListProductsResponse>(
            queryKey,
            (oldData) =>
              oldData && [
                ...oldData.map((item) =>
                  item.produto.cod_produto === data.cod_produto
                    ? { ...item, produto: data }
                    : {
                        ...item,
                        produto: {
                          ...item.produto,
                          produto_combo_list: (
                            item.produto.produto_combo_list ?? []
                          ).map((comboItem) =>
                            comboItem.produto.cod_produto === data.cod_produto
                              ? { ...comboItem, produto: data }
                              : comboItem,
                          ),
                        },
                      },
                ),
              ],
          )
        })

      toastPositive('Produto alterado com sucesso!')
    },
    onError: ({ response }: AxiosError<ApiError>, data) => {
      setFormErrors(
        handleErrors<FormUpdateProduct>(
          response?.data,
          Object.keys(data) as (keyof FormUpdateProduct)[],
          toastDestructive,
        ),
      )
    },
  })

  return { mutation, formErrors }
}
